import {
  ContainerDirection,
  ElementSizingMode,
  MarketingForgeElementType,
} from "./element-schema";

export type SizingAxis = "width" | "height";

export interface SizingInput {
  type: MarketingForgeElementType;
  widthSizing?: ElementSizingMode;
  widthFixedPx?: number;
  widthMinPx?: number;
  heightSizing?: ElementSizingMode;
  heightFixedPx?: number;
  heightMinPx?: number;
  growHeightWithContent?: boolean;
}

/** `px` is the size for `fixed` and the optional minimum for `hug`. */
export interface AxisSizing {
  mode: ElementSizingMode;
  px?: number;
}

/**
 * Flex for one child box. Minimums are explicit: a clipping flex item's
 * automatic minimum is 0 (Flexbox §4.5), so `auto` would silently squash.
 */
export interface ResolvedElementSizing {
  flex: string;
  alignSelf?: "stretch";
  width?: string;
  maxWidth?: string;
  height?: string;
  minWidth?: string;
  minHeight?: string;
}

export interface ChildSizingOptions {
  /** Direction the parent container actually renders in. */
  renderedDirection: "row" | "column";
  /** True when mobile force-stacked a horizontal container. */
  forceStacked: boolean;
}

export function mainAxisOf(direction: ContainerDirection): SizingAxis {
  return direction === "horizontal" ? "width" : "height";
}

function otherAxis(axis: SizingAxis): SizingAxis {
  return axis === "width" ? "height" : "width";
}

/** Only a leaf may hug, and only along its parent's main axis. */
export function allowedSizingModes(
  isContainer: boolean,
  isMainAxis: boolean,
): readonly ElementSizingMode[] {
  return !isContainer && isMainAxis
    ? (["hug", "fill", "fixed"] as const)
    : (["fill", "fixed"] as const);
}

function authoredSizing(
  element: SizingInput,
  axis: SizingAxis,
): { mode?: ElementSizingMode; fixedPx?: number; minPx?: number } {
  return axis === "width"
    ? {
        mode: element.widthSizing,
        fixedPx: element.widthFixedPx,
        minPx: element.widthMinPx,
      }
    : {
        mode: element.heightSizing,
        fixedPx: element.heightFixedPx,
        minPx: element.heightMinPx,
      };
}

/**
 * The mode an axis renders with. Absent sizing reproduces the pre-sizing
 * layout: hug down a column, fill everywhere else.
 */
export function resolveAxisSizing(
  element: SizingInput,
  axis: SizingAxis,
  parentMainAxis: SizingAxis,
): AxisSizing {
  const isMainAxis = axis === parentMainAxis;
  const { mode, fixedPx, minPx } = authoredSizing(element, axis);
  const requested = mode ?? (isMainAxis && axis === "height" ? "hug" : "fill");
  const allowed = allowedSizingModes(
    element.type === MarketingForgeElementType.CONTAINER,
    isMainAxis,
  );
  if (!allowed.includes(requested)) {
    return { mode: "fill" };
  }
  switch (requested) {
    case "hug":
      return { mode: "hug", px: minPx };
    case "fixed":
      // A fixed size with no px to size to would collapse the box.
      return fixedPx === undefined
        ? { mode: "fill" }
        : { mode: "fixed", px: fixedPx };
    case "fill":
      return { mode: "fill" };
  }
}

export function resolveChildSizing(
  element: SizingInput,
  { renderedDirection, forceStacked }: ChildSizingOptions,
): ResolvedElementSizing {
  // Sizing was authored against the horizontal axis; honoring it on the
  // stacked axis would reflow every spotlight that predates it.
  if (forceStacked) {
    return { flex: "0 0 auto", alignSelf: "stretch" };
  }
  const mainAxis: SizingAxis = renderedDirection === "row" ? "width" : "height";
  return {
    ...mainAxisStyles(element, mainAxis),
    ...crossAxisStyles(element, otherAxis(mainAxis), mainAxis),
  };
}

/** A growing container treats a fixed height as a floor rather than a cap. */
function heightIsFloor(element: SizingInput, axis: SizingAxis): boolean {
  return (
    axis === "height" &&
    element.type === MarketingForgeElementType.CONTAINER &&
    !!element.growHeightWithContent
  );
}

function mainAxisStyles(
  element: SizingInput,
  axis: SizingAxis,
): ResolvedElementSizing {
  const sizing = resolveAxisSizing(element, axis, axis);
  switch (sizing.mode) {
    case "hug":
      return {
        flex: "0 0 auto",
        ...(sizing.px !== undefined && minSize(axis, `${sizing.px}px`)),
      };
    case "fixed":
      if (heightIsFloor(element, axis)) {
        return { flex: "0 0 auto", minHeight: `${sizing.px ?? 0}px` };
      }
      // An explicit 0 minimum, or the item's automatic minimum lets its
      // content push it past the fixed size.
      return { flex: `0 0 ${sizing.px ?? 0}px`, ...minSize(axis, "0") };
    case "fill":
      // A Fill width shares the row exactly; content never widens it.
      if (axis === "width") {
        return { flex: "1 1 0%", minWidth: "0" };
      }
      return {
        flex: "1 1 0%",
        minHeight: clipsToFilledHeight(element) ? "0" : "min-content",
      };
  }
}

function minSize(
  axis: SizingAxis,
  value: string,
): Pick<ResolvedElementSizing, "minWidth" | "minHeight"> {
  return axis === "width" ? { minWidth: value } : { minHeight: value };
}

/** An image scales into its box and a non-growing container clips, so both may take less than their content. */
function clipsToFilledHeight(element: SizingInput): boolean {
  if (element.type === MarketingForgeElementType.IMAGE) {
    return true;
  }
  return (
    element.type === MarketingForgeElementType.CONTAINER &&
    !element.growHeightWithContent
  );
}

function crossAxisStyles(
  element: SizingInput,
  axis: SizingAxis,
  mainAxis: SizingAxis,
): Partial<ResolvedElementSizing> {
  const sizing = resolveAxisSizing(element, axis, mainAxis);
  if (sizing.mode !== "fixed" || sizing.px === undefined) {
    // The parent's alignment positions only fixed boxes; Fill always spans.
    return { alignSelf: "stretch" };
  }
  if (axis === "width") {
    return { width: `${sizing.px}px`, maxWidth: "100%" };
  }
  return heightIsFloor(element, axis)
    ? { minHeight: `${sizing.px}px` }
    : { height: `${sizing.px}px` };
}

/** Hug is only valid along the main axis, so a reorientation turns it into Fill. */
export function withoutCrossAxisHug<T extends SizingInput>(
  element: T,
  parentDirection: ContainerDirection,
): T {
  const crossAxis = otherAxis(mainAxisOf(parentDirection));
  if (crossAxis === "width" && element.widthSizing === "hug") {
    return { ...element, widthSizing: "fill" };
  }
  if (crossAxis === "height" && element.heightSizing === "hug") {
    return { ...element, heightSizing: "fill" };
  }
  return element;
}
